import { computed, Signal, WritableSignal } from '@angular/core';
import { Visit } from '@app/common-codes/models/matwins-tenant/Visit.model';
import { PointOfService } from '@app/common-codes/models/matwins-tenant/PointOfService.model';
import { VisitorType } from './ipad-visitor-type/ipad-visitor-type.component';

// --- Types ---
export type FieldValidator = (entity: Visit, pos?: PointOfService) => boolean;

export interface WizardFieldRule {
    fieldName: string;
    validate: FieldValidator;
}

export interface WizardStepDefinition {
    stepId: string;
    stepNumber: number;
    fields: WizardFieldRule[];
    /** Si retourne false, la step est complètement masquée (option POS désactivée) */
    isVisible: (pos?: PointOfService) => boolean;
    /** Si retourne true, la step est auto-skippée (données déjà présentes d'une ancienne visite) */
    canAutoSkip: (entity: Visit, pos?: PointOfService) => boolean;
}

// --- Classe principale ---
export class WizardStepValidator {
    private steps: WizardStepDefinition[] = [];

    constructor(
        private visitorType: VisitorType | null,
        private entitySignal: Signal<Visit | null | undefined>,
        private currentStepSignal: WritableSignal<number>,
        private pos?: PointOfService
    ) {
        this.steps = this.buildSteps();
    }

    /** Reconfigure les steps (ex: quand le POS ou le visitorType change) */
    rebuild(visitorType: VisitorType | null, pos?: PointOfService): void {
        this.visitorType = visitorType;
        this.pos = pos;
        this.steps = this.buildSteps();
    }

    /** Retourne uniquement les steps visibles selon la config POS */
    getVisibleSteps(): WizardStepDefinition[] {
        return this.steps.filter(s => s.isVisible(this.pos));
    }

    /** Retourne true si la step courante est entièrement valide */
    isCurrentStepValid(): boolean {
        const visibleSteps = this.getVisibleSteps();
        const current = visibleSteps.find(s => s.stepNumber === this.currentStepSignal());
        if (!current) return false;
        const entity = this.entitySignal();
        if (!entity) return false;
        return current.fields.every(f => f.validate(entity, this.pos));
    }

    /** Retourne true si une step donnée est valide */
    isStepValid(stepNumber: number): boolean {
        const step = this.steps.find(s => s.stepNumber === stepNumber);
        if (!step) return false;
        const entity = this.entitySignal();
        if (!entity) return false;
        return step.fields.every(f => f.validate(entity, this.pos));
    }

    /** Retourne la liste des champs invalides pour une step */
    getInvalidFields(stepNumber?: number): string[] {
        const sn = stepNumber ?? this.currentStepSignal();
        const step = this.steps.find(s => s.stepNumber === sn);
        if (!step) return [];
        const entity = this.entitySignal();
        if (!entity) return step.fields.map(f => f.fieldName);
        return step.fields
            .filter(f => !f.validate(entity, this.pos))
            .map(f => f.fieldName);
    }

    /** Crée un computed Angular signal pour canNext */
    createCanNextSignal(): Signal<boolean> {
        return computed(() => {
            const entity = this.entitySignal();
            const stepNumber = this.currentStepSignal();
            if (!entity) return false;
            return this.isStepValid(stepNumber);
        });
    }

    /**
     * Avance à la prochaine step visible et non auto-skippable.
     * Retourne le nouveau stepNumber, ou null si c'est la dernière (= save).
     */
    goForward(): number | null {
        const visibleSteps = this.getVisibleSteps();
        const currentIdx = visibleSteps.findIndex(s => s.stepNumber === this.currentStepSignal());
        const entity = this.entitySignal();

        for (let i = currentIdx + 1; i < visibleSteps.length; i++) {
            const candidate = visibleSteps[i];
            if (entity && candidate.canAutoSkip(entity, this.pos)) {
                continue; // skip cette step, les données sont déjà là
            }
            this.currentStepSignal.set(candidate.stepNumber);
            return candidate.stepNumber;
        }
        // Plus de step visible → on est à la fin (save)
        return null;
    }

    /**
     * Recule à la précédente step visible (ignore les auto-skip en arrière aussi).
     * Retourne le nouveau stepNumber, ou null si c'est la première (= go home).
     */
    goBack(): number | null {
        const visibleSteps = this.getVisibleSteps();
        const currentIdx = visibleSteps.findIndex(s => s.stepNumber === this.currentStepSignal());

        for (let i = currentIdx - 1; i >= 0; i--) {
            const candidate = visibleSteps[i];
            // En arrière, on ne skip pas automatiquement : l'utilisateur veut peut-être modifier
            this.currentStepSignal.set(candidate.stepNumber);
            return candidate.stepNumber;
        }
        return null; // première step → go home
    }

    /** Retourne true si on est sur la dernière step visible */
    isLastVisibleStep(): boolean {
        const visibleSteps = this.getVisibleSteps();
        const entity = this.entitySignal();
        const currentIdx = visibleSteps.findIndex(s => s.stepNumber === this.currentStepSignal());

        // Vérifie si toutes les steps restantes seraient auto-skippées
        for (let i = currentIdx + 1; i < visibleSteps.length; i++) {
            if (!entity || !visibleSteps[i].canAutoSkip(entity, this.pos)) {
                return false;
            }
        }
        return true;
    }

    // --- Construction des steps par visitorType ---
    private buildSteps(): WizardStepDefinition[] {
        switch (this.visitorType?.key) {
            case 'employee': return this.buildEmployeeSteps();
            case 'visitor': return this.buildVisitorSteps();
            case 'contractor': return this.buildContractorSteps();
            case 'group': return this.buildGroupSteps();
            default: return [];
        }
    }

    private buildEmployeeSteps(): WizardStepDefinition[] {
        return [
            {
                stepId: 'employee-identity',
                stepNumber: 1,
                isVisible: () => true,
                canAutoSkip: () => false,
                fields: [
                    { fieldName: 'visitor.fullname', validate: (e) => !!e.visitor?.fullname },
                    { fieldName: 'fK_Employee', validate: (e) => !!e.fK_Employee },
                ]
            },
            {
                stepId: 'employee-policy-signature',
                stepNumber: 2,
                isVisible: (pos) => !!pos?.dS_NewEmployee_Signature && !pos?.dS_ReturningEmployee_Skip_Signature,
                canAutoSkip: () => false,
                fields: [
                    {
                        fieldName: 'signature',
                        validate: (e) => !!(e.signatureFile || e.fK_Image_Signature),
                    }
                ]
            },
            {
                stepId: 'employee-engagement-signature',
                stepNumber: 3,
                isVisible: (pos) => !!pos?.dS_NewEmployee_Sign_Legal_Document,
                canAutoSkip: (e, pos) => {
                    const hasNoEngagements = !pos?.engagements?.length;
                    return hasNoEngagements;
                },
                fields: [
                    {
                        fieldName: 'signature',
                        validate: (e, pos) => {
                            const hasNoEngagements = !pos?.engagements?.length;
                            return hasNoEngagements || !!(e.signatureFile || e.fK_Image_Signature);
                        }
                    },
                    {
                        fieldName: 'allEngamgementChecked',
                        validate: (e, pos) => {
                            const hasNoEngagements = !pos?.engagements?.length;
                            return hasNoEngagements || !!e.allEngamgementChecked;
                        }
                    }
                ]
            },
            {
                stepId: 'employee-healthy',
                stepNumber: 4,
                isVisible: (pos) => !!pos?.dS_NewEmployee_Covid,
                canAutoSkip: (e, pos) => {
                    const hasNoCovids = !pos?.covids?.length;
                    return hasNoCovids || !!e.allCovidChecked;
                },
                fields: [
                    {
                        fieldName: 'allCovidChecked',
                        validate: (e, pos) => {
                            const hasNoCovids = !pos?.covids?.length;
                            return hasNoCovids || !!e.allCovidChecked;
                        }
                    }
                ]
            },
            {
                stepId: 'employee-quiz',
                stepNumber: 5,
                isVisible: (pos) => !!pos?.dS_Employee_Quiz,
                canAutoSkip: (e, pos) => {
                    const hasNoQuizzes = !pos?.quizzes?.length;
                    return hasNoQuizzes || e.quizStatus != null;
                },
                fields: [
                    {
                        fieldName: 'quizStatus',
                        validate: (e, pos) => {
                            const hasNoQuizzes = !pos?.quizzes?.length;
                            return hasNoQuizzes || e.quizStatus != null;
                        }
                    }
                ]
            }
        ];
    }

    private buildVisitorSteps(): WizardStepDefinition[] {
        return [
            {
                stepId: 'visitor-name',
                stepNumber: 1,
                isVisible: () => true,
                canAutoSkip: () => false, // toujours affiché
                fields: [
                    { fieldName: 'visitor.fullname', validate: (e) => !!e.visitor?.fullname },
                    { fieldName: 'company', validate: (e, pos) => !pos?.dS_NewVisitor_CompanyRequired || !!e.company },
                ]
            },
            {
                stepId: 'visitor-employee',
                stepNumber: 2,
                isVisible: () => true,
                canAutoSkip: (e) => !!e.fK_Employee, // skip si employé déjà récupéré de la dernière visite
                fields: [
                    { fieldName: 'fK_Employee', validate: (e) => !!e.fK_Employee },
                ]
            },
            {
                stepId: 'visitor-policy-signature',
                stepNumber: 3,
                isVisible: (pos) => {
                    // Visible seulement si le POS a une politique OU requiert une signature
                    return !!pos?.dS_NewVisitor_Signature || !pos?.dS_Visitor_Skip_Signature;
                },
                canAutoSkip: (e, pos) => {
                    const hasNoPolicies = !pos?.policies?.length;
                    const hasSignedBefore = !!e.previousVisit?.fK_Image_Signature;
                    return hasNoPolicies || (!!pos?.dS_Visitor_Skip_Signature && hasSignedBefore);
                },
                fields: [
                    {
                        fieldName: 'signature',
                        validate: (e, pos) => {
                            const hasNoPolicies = !pos?.policies?.length;

                            const isNew = !e.previousVisit;
                            const hasSignedBefore = !!e.previousVisit?.fK_Image_Signature;
                            const signatureRequired =
                                (isNew && pos?.dS_NewVisitor_Signature) ||
                                (!isNew && !pos?.dS_Visitor_Skip_Signature) ||
                                (!isNew && pos?.dS_Visitor_Skip_Signature && !hasSignedBefore);
                            const hasSignature = !!(e.signatureFile || e.fK_Image_Signature);
                            return hasNoPolicies || !signatureRequired || hasSignature;
                        }
                    }
                ]
            },
            {
                stepId: 'visitor-healthy',
                stepNumber: 4,
                isVisible: (pos) => !!pos?.dS_NewVisitor_Covid,
                canAutoSkip: (e, pos) => {
                    const hasNoCovids = !pos?.covids?.length;
                    return hasNoCovids || !!e.allCovidChecked;
                },
                fields: [
                    {
                        fieldName: 'allCovidChecked',
                        validate: (e, pos) => {
                            const hasNoCovids = !pos?.covids?.length;
                            return hasNoCovids || !!e.allCovidChecked;
                        }
                    }
                ]
            },
            {
                stepId: 'visitor-quiz',
                stepNumber: 5,
                isVisible: (pos) => !!pos?.dS_Visitor_Quiz,
                canAutoSkip: (e, pos) => {
                    const hasNoQuizzes = !pos?.quizzes?.length;
                    return hasNoQuizzes || e.quizStatus != null;
                },
                fields: [
                    {
                        fieldName: 'quizStatus',
                        validate: (e, pos) => {
                            const hasNoQuizzes = !pos?.quizzes?.length;
                            return hasNoQuizzes || e.quizStatus != null;
                        }
                    }
                ]
            }
        ];
    }

    private buildGroupSteps(): WizardStepDefinition[] {
        return [
            {
                stepId: 'group-employee',
                stepNumber: 1,
                isVisible: () => true,
                canAutoSkip: () => false,
                fields: [
                    { fieldName: 'fK_Employee', validate: (e) => !!e.fK_Employee },
                ]
            },
            {
                stepId: 'group-visitor',
                stepNumber: 2,
                isVisible: () => true,
                canAutoSkip: () => false,
                fields: [
                    { fieldName: 'visitor.fullname', validate: (e) => !!e.visitor?.fullname },
                    { fieldName: 'company', validate: (e, pos) => !pos?.dS_NewVisitor_CompanyRequired || !!e.company },
                ]
            },
            {
                stepId: 'group-policy-signature',
                stepNumber: 3,
                isVisible: (pos) => {
                    return !!pos?.dS_NewVisitor_Signature || !pos?.dS_Visitor_Skip_Signature;
                },
                canAutoSkip: (e, pos) => {
                    const hasNoPolicies = !pos?.policies?.length;
                    const hasSignedBefore = !!e.previousVisit?.fK_Image_Signature;
                    return hasNoPolicies || (!!pos?.dS_Visitor_Skip_Signature && hasSignedBefore);
                },
                fields: [
                    {
                        fieldName: 'signature',
                        validate: (e, pos) => {
                            const hasNoPolicies = !pos?.policies?.length;

                            const isNew = !e.previousVisit;
                            const hasSignedBefore = !!e.previousVisit?.fK_Image_Signature;
                            const signatureRequired =
                                (isNew && pos?.dS_NewVisitor_Signature) ||
                                (!isNew && !pos?.dS_Visitor_Skip_Signature) ||
                                (!isNew && pos?.dS_Visitor_Skip_Signature && !hasSignedBefore);
                            const hasSignature = !!(e.signatureFile || e.fK_Image_Signature);
                            return hasNoPolicies || !signatureRequired || hasSignature;
                        }
                    }
                ]
            },
            {
                stepId: 'group-healthy',
                stepNumber: 4,
                isVisible: (pos) => !!pos?.dS_NewVisitor_Covid,
                canAutoSkip: (e, pos) => {
                    const hasNoCovids = !pos?.covids?.length;
                    return hasNoCovids || !!e.allCovidChecked;
                },
                fields: [
                    {
                        fieldName: 'allCovidChecked',
                        validate: (e, pos) => {
                            const hasNoCovids = !pos?.covids?.length;
                            return hasNoCovids || !!e.allCovidChecked;
                        }
                    }
                ]
            },
            {
                stepId: 'group-quiz',
                stepNumber: 5,
                isVisible: (pos) => !!pos?.dS_Visitor_Quiz,
                canAutoSkip: (e, pos) => {
                    const hasNoQuizzes = !pos?.quizzes?.length;
                    return hasNoQuizzes || e.quizStatus != null;
                },
                fields: [
                    {
                        fieldName: 'quizStatus',
                        validate: (e, pos) => {
                            const hasNoQuizzes = !pos?.quizzes?.length;
                            return hasNoQuizzes || e.quizStatus != null;
                        }
                    }
                ]
            }
        ];
    }

    private buildContractorSteps(): WizardStepDefinition[] {
        return [
            {
                stepId: 'contractor-name',
                stepNumber: 1,
                isVisible: () => true,
                canAutoSkip: () => false,
                fields: [
                    { fieldName: 'visitor.fullname', validate: (e) => !!e.visitor?.fullname },
                ]
            },
            {
                stepId: 'contractor-employee',
                stepNumber: 2,
                isVisible: () => true,
                canAutoSkip: (e) => !!e.fK_Employee,
                fields: [
                    { fieldName: 'fK_Employee', validate: (e) => !!e.fK_Employee },
                ]
            },
            {
                stepId: 'contractor-policy-signature',
                stepNumber: 3,
                isVisible: (pos) => !!pos?.dS_NewVisitor_Signature || !pos?.dS_Contractor_Skip_Signature,
                canAutoSkip: () => false,
                fields: [
                    {
                        fieldName: 'signature',
                        validate: (e) => !!(e.signatureFile || e.fK_Image_Signature),
                    }
                ]
            },
            {
                stepId: 'contractor-engagement-signature',
                stepNumber: 4,
                isVisible: (pos) => !!pos?.dS_Contractor_Enforce_Document_Signature,
                canAutoSkip: (e, pos) => {
                    const hasNoEngagements = !pos?.engagements?.length;
                    return hasNoEngagements;
                },
                fields: [
                    {
                        fieldName: 'signature',
                        validate: (e, pos) => {
                            const hasNoEngagements = !pos?.engagements?.length;
                            return hasNoEngagements || !!(e.signatureFile || e.fK_Image_Signature);
                        }
                    },
                    {
                        fieldName: 'allEngamgementChecked',
                        validate: (e, pos) => {
                            const hasNoEngagements = !pos?.engagements?.length;
                            return hasNoEngagements || !!e.allEngamgementChecked;
                        }
                    }
                ]
            },
            {
                stepId: 'visitor-healthy',
                stepNumber: 5,
                isVisible: (pos) => !!pos?.dS_NewVisitor_Covid,
                canAutoSkip: (e, pos) => {
                    const hasNoCovids = !pos?.covids?.length;
                    return hasNoCovids || !!e.allCovidChecked;
                },
                fields: [
                    {
                        fieldName: 'allCovidChecked',
                        validate: (e, pos) => {
                            const hasNoCovids = !pos?.covids?.length;
                            return hasNoCovids || !!e.allCovidChecked;
                        }
                    }
                ]
            },
            {
                stepId: 'contractor-quiz',
                stepNumber: 6,
                isVisible: (pos) => !!pos?.dS_Contractor_Quiz,
                canAutoSkip: (e, pos) => {
                    const hasNoQuizzes = !pos?.quizzes?.length;
                    return hasNoQuizzes || e.quizStatus != null;
                },
                fields: [
                    {
                        fieldName: 'quizStatus',
                        validate: (e, pos) => {
                            const hasNoQuizzes = !pos?.quizzes?.length;
                            return hasNoQuizzes || e.quizStatus != null;
                        }
                    }
                ]
            }
        ];
    }
}